import { AvertissementSalle } from './AvertissementSalle.js';
import { ApiError } from '../../api/errors.js';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Switch } from '@artosera/ui';
import type { ContactPresente } from '../../api/contacts.js';
import { useCreerSalle } from '../../api/viewingRooms.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { dateFinJour, dateSaisie } from './salleUi.js';
import styles from './NouvelleSalleDialog.module.css';

export function NouvelleSalleDialog({
  galerieSlug,
  ouvert,
  onFermer,
  oeuvreInitiale,
  oeuvresInitiales,
  titreInitial = '',
}: {
  galerieSlug: string;
  ouvert: boolean;
  onFermer: () => void;
  oeuvreInitiale?: { id: string; titre: string };
  oeuvresInitiales?: readonly { id: string; titre: string }[];
  titreInitial?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const navigate = useNavigate();
  const creation = useCreerSalle(galerieSlug);
  const [titre, setTitre] = useState(titreInitial);
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [oeuvres, setOeuvres] = useState(
    oeuvresInitiales ? [...oeuvresInitiales] : oeuvreInitiale ? [oeuvreInitiale] : [],
  );
  const [recherche, setRecherche] = useState(false);
  const [prixVisibles, setPrixVisibles] = useState(false);
  const [maintenant] = useState(() => Date.now());
  const [expireLe, setExpireLe] = useState(() =>
    dateSaisie(new Date(maintenant + 14 * 86_400_000)),
  );
  const [erreur, setErreur] = useState<'generique' | 'expiration_invalide' | null>(null);
  return (
    <Dialog
      open={ouvert}
      onOpenChange={(open) => {
        if (!open) onFermer();
      }}
      title={t('regie:viewingRooms.nouvelle')}
      closeLabel={t('regie:viewingRooms.fermer')}
      description={t('regie:viewingRooms.sousTitre')}
    >
      <form
        className={styles.formulaire}
        onSubmit={(event) => {
          event.preventDefault();
          if (!titre.trim() || !oeuvres.length || creation.isPending) return;
          setErreur(null);
          creation.mutate(
            {
              titre: { fr: titre.trim() },
              oeuvres: oeuvres.map((o) => o.id),
              contactId: contact?.id ?? null,
              expireLe: new Date(
                Math.min(new Date(dateFinJour(expireLe)).getTime(), maintenant + 90 * 86_400_000),
              ).toISOString(),
              prixVisibles,
            },
            {
              onSuccess: (salle) => {
                onFermer();
                void navigate(`/${galerieSlug}/viewing-rooms/${salle.id}`);
              },
              onError: (e) =>
                setErreur(
                  e instanceof ApiError && JSON.stringify(e.details).includes('expiration_invalide')
                    ? 'expiration_invalide'
                    : 'generique',
                ),
            },
          );
        }}
      >
        <label className={styles.champ} htmlFor={`${id}-titre`}>
          {t('regie:viewingRooms.champs.titre')}
          <Input
            id={`${id}-titre`}
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            maxLength={200}
            required
          />
        </label>
        <div className={styles.champ}>
          <span>{t('regie:viewingRooms.champs.contact')}</span>
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={contact}
            onChoisir={setContact}
            onEffacer={() => setContact(null)}
          />
        </div>
        <div className={styles.champ}>
          <span>
            {t('regie:viewingRooms.champs.oeuvres')} · {oeuvres.length} / 40
          </span>
          <ul className={styles.oeuvres}>
            {oeuvres.map((oeuvre) => (
              <li key={oeuvre.id}>
                <div>
                  <span>{oeuvre.titre}</span>
                  <AvertissementSalle galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />
                </div>
                <Button
                  variant="ghost"
                  aria-label={t('regie:viewingRooms.retirerOeuvre', { titre: oeuvre.titre })}
                  onClick={() => setOeuvres(oeuvres.filter((o) => o.id !== oeuvre.id))}
                >
                  ×
                </Button>
              </li>
            ))}
          </ul>
          {recherche ? (
            <RechercheOeuvre
              galerieSlug={galerieSlug}
              onChoisir={(o) => {
                if (oeuvres.length < 40 && !oeuvres.some((v) => v.id === o.id))
                  setOeuvres([...oeuvres, { id: o.id, titre: o.titre }]);
                setRecherche(false);
              }}
            />
          ) : (
            <Button
              variant="secondary"
              disabled={oeuvres.length >= 40}
              onClick={() => setRecherche(true)}
            >
              {t('regie:viewingRooms.ajouterOeuvre')}
            </Button>
          )}
        </div>
        <label className={styles.champ} htmlFor={`${id}-date`}>
          {t('regie:viewingRooms.champs.expireLe')}
          <Input
            id={`${id}-date`}
            type="date"
            value={expireLe}
            min={dateSaisie(new Date(maintenant))}
            max={dateSaisie(new Date(maintenant + 90 * 86_400_000))}
            onChange={(e) => setExpireLe(e.target.value)}
            required
          />
        </label>
        <Switch
          id={`${id}-prix`}
          checked={prixVisibles}
          onCheckedChange={setPrixVisibles}
          label={t('regie:viewingRooms.champs.prixVisibles')}
        />
        {erreur && <p role="alert">{t(`regie:viewingRooms.erreurs.${erreur}`)}</p>}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onFermer}>
            {t('regie:viewingRooms.annuler')}
          </Button>
          <Button
            type="submit"
            disabled={!titre.trim() || oeuvres.length === 0 || !expireLe || creation.isPending}
            loading={creation.isPending}
          >
            {t('regie:viewingRooms.creer')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
